// Merchant Sales Report (MM-3037): the merchant picker in the table toolbar ("Search by name" / "Search by
// merchant code", rotating like the other listing pages). Searches Master Distributors and Distributors by name or user code on the server.
import React, { useEffect, useRef, useState } from "react";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import DropdownShimmer from "../../../components/Dropdowns/DropdownShimmer";
import Image from "../../../components/images/Image";
import { MERCHANT_TYPE_LABELS, MerchantOption } from "./types";

export const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 300;
// Fetch the next page once the list is scrolled within this many px of its end (as SearchWithDropdown does).
const LOAD_MORE_THRESHOLD = 20;
// Rotating "Search by …" placeholder, same timings as the other listing pages (fade class: .search-placeholder).
const SEARCH_FIELDS = ["name", "merchant code"];
const ROTATE_MS = 2500;
const FADE_MS = 500;

/** Cycles through SEARCH_FIELDS; `fading` is true while the old text fades out. */
const useRotatingPlaceholder = () => {
  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    let fadeTimer: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      setFading(true);
      fadeTimer = setTimeout(() => {
        setIndex((i) => (i + 1) % SEARCH_FIELDS.length);
        setFading(false);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(fadeTimer);
    };
  }, []);
  return { placeholder: `Search by ${SEARCH_FIELDS[index]}`, fading };
};

interface MerchantSearchProps {
  /** Text shown in the box once a merchant is selected. */
  selectedLabel: string;
  /** One page (1-based) of matches for the query, and whether more pages follow. */
  fetchOptions: (query: string, page: number) => Promise<{ items: MerchantOption[]; hasMore: boolean }>;
  onSelect: (option: MerchantOption) => void;
  /** Called with the query when a finished search returns no merchants, and with null when it no longer applies. */
  onNoResults: (query: string | null) => void;
  onClear: () => void;
  /** Bumped by the page to reset the box ("Clear search & filters"). */
  resetKey: number;
}

const MerchantSearch = ({ selectedLabel, fetchOptions, onSelect, onNoResults, onClear, resetKey }: MerchantSearchProps) => {
  const [query, setQuery] = useState(selectedLabel);
  const [options, setOptions] = useState<MerchantOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [focused, setFocused] = useState(false);
  // Infinite scroll: the last page loaded for the current query and whether the server has more.
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { placeholder, fading } = useRotatingPlaceholder();
  const requestId = useRef(0);
  // Kept in refs so the debounce effect only re-runs when the text changes.
  const fetchRef = useRef(fetchOptions);
  fetchRef.current = fetchOptions;
  const noResultsRef = useRef(onNoResults);
  noResultsRef.current = onNoResults;

  useEffect(() => {
    setQuery(selectedLabel);
    setDirty(false);
    setOpen(false);
  }, [selectedLabel, resetKey]);

  useEffect(() => {
    if (!dirty) return;
    const q = query.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      requestId.current++;
      setOptions([]);
      setLoading(false);
      noResultsRef.current(null);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setLoadingMore(false);
    const timer = setTimeout(async () => {
      try {
        const { items: results, hasMore: more } = await fetchRef.current(q, 1);
        if (id !== requestId.current) return;
        setOptions(results);
        setPage(1);
        setHasMore(more);
        setOpen(results.length > 0);
        noResultsRef.current(results.length === 0 ? q : null);
      } catch {
        if (id === requestId.current) setOptions([]);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, dirty]);

  const loadMore = async () => {
    if (loading || loadingMore || !hasMore) return;
    // Same request id as the page-1 search, so typing again drops this page when it lands.
    const id = requestId.current;
    setLoadingMore(true);
    try {
      const { items, hasMore: more } = await fetchRef.current(query.trim(), page + 1);
      if (id !== requestId.current) return;
      setOptions((prev) => [...prev, ...items.filter((item) => !prev.some((p) => p.id === item.id))]);
      setPage(page + 1);
      setHasMore(more);
    } catch {
      // Keep what is listed; scrolling again retries.
    } finally {
      if (id === requestId.current) setLoadingMore(false);
    }
  };

  const onListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD) loadMore();
  };

  const select = (option: MerchantOption) => {
    requestId.current++;
    setOpen(false);
    setLoading(false);
    setDirty(false);
    noResultsRef.current(null);
    onSelect(option);
  };

  const clear = () => {
    requestId.current++;
    setQuery("");
    setOptions([]);
    setOpen(false);
    setLoading(false);
    setDirty(false);
    noResultsRef.current(null);
    onClear();
  };

  return (
    <div className="relative w-full max-w-[310px]">
      {/* Same box as the shared InputFieldwithSearch used by the other listing pages. */}
      <label className="flex h-10 items-center justify-between gap-2 rounded-lg border border-P_light_active bg-white pl-2.5 focus-within:border-2 focus-within:pl-[9px]">
        <Image src="active_search" className="h-5 w-5 shrink-0" />
        <input
          className={`w-full min-w-0 grow bg-transparent text-body2 outline-hidden ${
            query ? "" : `search-placeholder ${fading ? "transitioning" : ""}`
          }`}
          placeholder={placeholder}
          aria-label="Search merchant by name or merchant code"
          // The placeholder contains "name", so Chrome's heuristics classify this box as a person's name and pop up
          // saved addresses over our own list. Chrome ignores autocomplete="off" (and unrecognised tokens) for
          // address autofill, but its form parser skips every non-select field with role="presentation"
          // (IsRelevant() in chromium components/autofill/core/browser/form_parsing/form_field_parser.cc), so the
          // field never gets an address type. Screen readers still see a textbox: a presentation role is ignored
          // on a focusable element.
          autoComplete="off"
          spellCheck={false}
          maxLength={100}
          role="presentation"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setDirty(true);
          }}
          onFocus={() => {
            setFocused(true);
            setOpen(options.length > 0 && dirty);
          }}
          onBlur={() => {
            setFocused(false);
            setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key === "Enter" && open && options[0]) select(options[0]);
          }}
        />
        {query && (
          <button type="button" aria-label="Clear search" className="shrink-0 pl-2" onClick={clear}>
            <Image src="close" className="my-2.5 mr-[15px] h-5 w-5 cursor-pointer" />
          </button>
        )}
      </label>
      {/* Same panel and rows as the shared SearchWithDropdown: shimmer while searching, then the matches. */}
      {(open || (loading && focused)) && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 rounded-lg border border-P_light_active bg-white p-3.5">
          {loading ? (
            <DropdownShimmer count={3} />
          ) : (
            <ul
              role="listbox"
              className={`max-h-[240px] overflow-x-hidden overflow-y-auto p-1 ${getFirefoxCssFix("scroll")}`}
              onScroll={onListScroll}
              data-testid="merchant-search-options"
            >
              {options.map((option) => (
                <li
                  key={option.id}
                  role="option"
                  aria-selected={false}
                  className="cursor-pointer p-2.5 text-primary_text hover:rounded-lg hover:bg-information_light"
                  // mousedown, not click: the input's blur would close the list before a click lands.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(option);
                  }}
                >
                  <div className="truncate text-subTitle">{option.name}</div>
                  <div className="text-xs text-secondary_text">
                    {option.user_code} · {MERCHANT_TYPE_LABELS[option.user_type] ?? option.user_type}
                  </div>
                </li>
              ))}
              {loadingMore && <li className="py-2 text-center text-sm text-secondary_text">Loading more...</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default MerchantSearch;
